import { useState } from 'react';
import {
  useSllDeleteModuleContentMutation,
  useSllDeleteModuleMutation,
  useSllUpdateVideoModuleMutation,
  useSllUpdateModuleMutation,
  SllModuleFragmentDoc,
  SllLessonForSaFragmentDoc,
} from '@api/graphql-codegen';
import { useEditLesson } from '../../useEditLesson';
import { ContentType, DefaultModuleEnum, ExtendedSllModule, SllSection, SllModule } from '@types';
import { logger } from '../../../../../utils/logger';

export interface useSchoolModuleCRUDProps {
  currentTabContent?: ExtendedSllModule;
  groups: ExtendedSllModule[];
  clearFile: () => void;
  activeTab: string;
  isGroupContent: boolean;
  onChangeData: (v: string | null) => void;
  onChangeGroupTabs: (v: ExtendedSllModule[]) => void;
};

export const useSchoolModuleCRUD = ({ currentTabContent, groups, clearFile, activeTab, isGroupContent, onChangeData, onChangeGroupTabs }: useSchoolModuleCRUDProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { content, lesson, setSelectionLevel, setLesson } = useEditLesson();

  const [deleteModuleData, { loading: deletingModuleData }] = useSllDeleteModuleContentMutation({
    update(cache, res) {
      if (res.data?.sllDeleteModuleContent) {
        cache.updateFragment({
          id: `SLLModule:${currentTabContent?.id}`,
          fragment: SllModuleFragmentDoc,
        }, (c) => ({ ...c, data: null }))
        onChangeData(null);
        const newGroups = [...groups];
        newGroups[0].data = null;
        onChangeGroupTabs(newGroups);
      }
    }
  });

  const [removeContent, { loading: isRemovingModule }] = useSllDeleteModuleMutation({
    update(_cache, res) {
      setLoading(false);
      if (res.data?.sllDeleteModule) {
        if (lesson?.id) {
          const classPresentation = [...(lesson.presentation || [])]
          const materials = [...(lesson.materials || [])]
          const contentIndex = classPresentation.findIndex(m => m?.id === content?.id);
          const materialContentIndex = materials.findIndex(m => m?.id === content?.id);
          if (contentIndex > -1) {
              classPresentation.splice(contentIndex, 1);
              setLesson({...lesson, presentation: classPresentation});
              _cache.updateFragment({
                id: `SLLLesson:${lesson?.id}`,
                fragment: SllLessonForSaFragmentDoc,
                fragmentName: 'SLLLesson'
              }, (data) =>({
                ...data,
                presentation: classPresentation,
              }));
          } else if (materialContentIndex > -1) {
            materials.splice(contentIndex, 1);
            setLesson({...lesson, materials});
            _cache.updateFragment({
              id: `SLLLesson:${lesson?.id}`,
              fragment: SllLessonForSaFragmentDoc,
              fragmentName: 'SLLLesson'
            }, (data) =>({
              ...data,
              materials,
            }));
          } else if (lesson?.sections?.length) {
            const newSections = lesson.sections;
            for (const section of newSections) {
              const sectionModuleIndex = section?.modules?.findIndex(m => m?.id === content?.id);
              if (sectionModuleIndex! > -1) {
                (section?.modules || [])?.splice(sectionModuleIndex!, 1);
                classPresentation.splice(contentIndex, 1);
                setLesson({...lesson, sections: newSections});
                _cache.updateFragment({
                  id: `SLLLesson:${lesson?.id}`,
                  fragment: SllLessonForSaFragmentDoc,
                  fragmentName: 'SLLLesson'
                }, (data) =>({
                  ...data,
                  sections: newSections,
                }));
                break;
              }
            }
          }
        }
      }
    },
    onCompleted() {
      if (lesson?.id) {
        setSelectionLevel('Lesson');
      }
    },
    onError(e) {
      setLoading(false);
      logger.error({ e });
      setSelectionLevel('Lesson');
    },
  });

  const [updateVideoContent] = useSllUpdateVideoModuleMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      setLoading(false);
    },
    update(cache, res) {
      const d = res?.data?.sllUpdateVideoModule;
      if (d) {
        cache.updateFragment({
          id: d.id,
          fragment: SllModuleFragmentDoc,
        }, (data) => ({
          ...data, data: d.data, meta: d.meta
        }));
        if (d?.data) {
          onChangeData(d?.data)
        }
      }
      clearFile();
    },
    onCompleted() {
      setLoading(false);
    }
  });

  const [updateContent] = useSllUpdateModuleMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      setLoading(false);
    },
    update(cache, res) {
      const d = res.data?.sllUpdateModule;

      if (d) {
        cache.updateFragment({
          id: d.id,
          fragment: SllModuleFragmentDoc,
        }, (data) =>({
          ...data, name: d.name,
        }));
        if (isGroupContent) {
          setLoading(false);
          return;
        }
        if (d.type !== ContentType.FORM) {
          onChangeData(d?.data ?? '');
        }
        const newGroupsTabs = [...groups];
        const index = newGroupsTabs.findIndex(a => a.groupName === activeTab);
        if (d.type === ContentType.FORM && activeTab !== DefaultModuleEnum.Default) {
          newGroupsTabs[index] = { ...newGroupsTabs[index], name: d.name, data: newGroupsTabs[index].data, meta: d.meta,
            formData: d.formData, position: d.position, lastInGroup: d.lastInGroup, lastInLesson: d.lastInLesson, isRequired: d.isRequired };
        } else {
          if (activeTab === DefaultModuleEnum.Default) {
            newGroupsTabs[index] = { ...newGroupsTabs[index], ...d };
          } else {
            newGroupsTabs[index] = { ...newGroupsTabs[index], name: d.name, meta: d.meta,
              formData: d.formData, position: d.position, lastInGroup: d.lastInGroup, lastInLesson: d.lastInLesson, isRequired: d.isRequired };
          }
        }
        onChangeGroupTabs(newGroupsTabs);
        clearFile();

        if (lesson?.id) {
          const classPresentation = [...(lesson.presentation || [])]
          const materials = [...(lesson.materials || [])]
          const contentIndex = classPresentation.findIndex(m => m?.id === d?.id);
          const materialContentIndex = materials.findIndex(m => m?.id === d?.id);
          if (contentIndex > -1) {
              classPresentation[contentIndex] = d;
              setLesson({...lesson, presentation: classPresentation});
              cache.updateFragment({
                id: `SLLLesson:${lesson?.id}`,
                fragment: SllLessonForSaFragmentDoc,
                fragmentName: 'SLLLesson'
              }, (data) =>({
                ...data,
                presentation: classPresentation,
              }));
          } else if (materialContentIndex > -1) {
            materials[materialContentIndex] = d;
            setLesson({...lesson, materials});
            cache.updateFragment({
              id: `SLLLesson:${lesson?.id}`,
              fragment: SllLessonForSaFragmentDoc,
              fragmentName: 'SLLLesson'
            }, (data) =>({
              ...data,
              materials,
            }));
          } else if (lesson?.sections?.length) {
            const newSections = lesson.sections;
            for (let i = 0; i < newSections?.length; i++) {
              const sectionModuleIndex = newSections[i]?.modules?.findIndex(m => m?.id === content?.id);
              if (sectionModuleIndex !== undefined && sectionModuleIndex! > -1 && newSections[i] && newSections[i]?.modules?.[sectionModuleIndex]) {
                const modules = (newSections[i] as SllSection).modules as SllModule[];
                modules[sectionModuleIndex] = d;
                (newSections[i] as SllSection).modules = modules;
                setLesson({...lesson, sections: newSections});
                cache.updateFragment({
                  id: `SLLLesson:${lesson?.id}`,
                  fragment: SllLessonForSaFragmentDoc,
                  fragmentName: 'SLLLesson'
                }, (data) =>({
                  ...data,
                  sections: newSections,
                }));
                break;
              }
            }
          }
        }
      }
      setLoading(false);
    }
  });


  return {
    updateContent,
    updateVideoContent,
    deleteModuleData,
    removeContent,
    loading,
    isRemovingModule,
    deletingModuleData
  }
};
